import React from 'react'
import { useTranslation } from 'react-i18next'
import { ViewModeToggle } from 'src/components/view-mode-toggle'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { cn } from 'src/utils/style'

interface ListToolbarProps {
  /** Simple filters (e.g. a `SegmentedControl`), left-aligned. */
  filters?: React.ReactNode
  /** Total item count; hidden when undefined. */
  count?: number
  viewMode?: ViewMode
  onViewModeChange?: (mode: ViewMode) => void
  className?: string
}

/**
 * Toolbar placed directly above the list it controls: simple filters left,
 * item count + grid/list toggle right. Renders nothing when it has nothing to show.
 */
export const ListToolbar = ({
  filters,
  count,
  viewMode,
  onViewModeChange,
  className,
}: ListToolbarProps) => {
  const { t } = useTranslation()
  const showToggle = viewMode !== undefined && onViewModeChange !== undefined
  if (!filters && count === undefined && !showToggle) return null

  return (
    <div
      className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-2', className)}
      data-testid="list-toolbar"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">{filters}</div>
      <div className="ml-auto flex items-center gap-3">
        {count !== undefined && (
          <span className="text-sm text-text-secondary" data-testid="list-toolbar-count">
            {t('common.itemCount', { count })}
          </span>
        )}
        {showToggle && <ViewModeToggle value={viewMode} onChange={onViewModeChange} />}
      </div>
    </div>
  )
}
